import { AfterViewInit, Directive, ElementRef, OnDestroy, Renderer2 } from '@angular/core';

@Directive({ selector: '[appTableExport]' })
export class TableExportDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;
  private styleElement: HTMLStyleElement | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {
    this.addStyles();
  }

  ngAfterViewInit() {
    this.processTables();
    // Use MutationObserver to handle dynamic/streaming content
    this.observer = new MutationObserver(() => this.processTables());
    this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  private processTables() {
    const tables = this.el.nativeElement.querySelectorAll('table:not(.csv-export-processed)');
    tables.forEach((table: HTMLTableElement) => {
      table.classList.add('csv-export-processed');
      this.wrapTableWithExport(table);
    });
  }

  private wrapTableWithExport(table: HTMLTableElement) {
    // Create wrapper
    const wrapper = this.renderer.createElement('div');
    this.renderer.addClass(wrapper, 'table-export-wrapper');

    // Create export button
    const button = this.renderer.createElement('button');
    this.renderer.addClass(button, 'table-export-btn');
    this.renderer.setAttribute(button, 'type', 'button');
    this.renderer.setAttribute(button, 'aria-label', 'Export as CSV');
    this.renderer.setAttribute(button, 'data-tooltip', 'Export as CSV');

    // Create icon using Material Icons font
    const icon = this.renderer.createElement('span');
    this.renderer.addClass(icon, 'material-icons');
    this.renderer.setProperty(icon, 'textContent', 'download');
    this.renderer.appendChild(button, icon);

    // Add click handler
    this.renderer.listen(button, 'click', (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.exportTableToCSV(table);
    });

    // Insert wrapper before table
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(button);
      wrapper.appendChild(table);
    }
  }

  private exportTableToCSV(table: HTMLTableElement) {
    const rows = table.querySelectorAll('tr');
    const csvRows: string[] = [];

    rows.forEach(row => {
      const cells = row.querySelectorAll('th, td');
      const rowData = Array.from(cells).map(cell => {
        let text = (cell.textContent || '').trim();
        // Escape quotes and wrap if contains special characters
        if (text.includes(',') || text.includes('"') || text.includes('\n')) {
          text = `"${text.replace(/"/g, '""')}"`;
        }
        return text;
      });
      csvRows.push(rowData.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `table-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private addStyles() {
    // Add the styles to the document if they don't exist
    if (!document.getElementById('table-export-styles')) {
      const styleEl = this.renderer.createElement('style');
      this.renderer.setProperty(styleEl, 'id', 'table-export-styles');
      const styles = `
        .table-export-wrapper {
          position: relative;
          margin: 1em 0;
        }

        .table-export-wrapper table {
          margin: 0;
        }

        .table-export-btn {
          position: absolute;
          top: 6px;
          right: 7px;
          width: 28px;
          height: 28px;
          padding: 0;
          border: none;
          border-radius: 4px;
          background-color: rgba(var(--accent-rgb), 0.1);
          color: var(--accent);
          cursor: pointer;
          transition: background-color 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }

        .table-export-btn:hover {
          background-color: rgba(var(--accent-rgb), 0.2);
        }

        .table-export-btn:focus {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }

        .table-export-btn .material-icons {
          font-size: 18px;
        }

        .table-export-btn::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 6px;
          padding: 6px 10px;
          background-color: #616161;
          color: white;
          font-size: 12px;
          font-family: inherit;
          white-space: nowrap;
          border-radius: 4px;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.15s ease, visibility 0.15s ease;
          pointer-events: none;
          z-index: 1000;
        }

        .table-export-btn:hover::after {
          opacity: 1;
          visibility: visible;
        }
      `;

      this.renderer.setProperty(styleEl, 'textContent', styles);
      this.renderer.appendChild(document.head, styleEl);
      this.styleElement = styleEl;
    }
  }
}
